/**
 * Factoría de proveedores EXTERNOS (enlace validado): Zoom / Meet / custom.
 *
 * No embeben: devuelven un `JoinDescriptor` `external` con una URL https
 * validada. La URL viene de la sesión (`meetingUrl`, enlace por-sesión que pone
 * el profesor) o, en su defecto, de la config del proveedor (`url`, p.ej. una
 * sala personal fija). `buildJoin` es puro y lanza si la URL no es https válida.
 */

import {
  type JoinDescriptor,
  type JoinUser,
  type LiveSession,
  type MeetingProvider,
  type MeetingProviderManifest,
  validateHttpsUrl,
} from "../registry";

export interface ExternalProviderOptions {
  id: string;
  name: string;
  description: string;
  requiresCredentials?: boolean;
  urlPlaceholder?: string;
}

/** Crea un proveedor externo estándar (Zoom/Meet/custom comparten la lógica). */
export function makeExternalProvider(opts: ExternalProviderOptions): MeetingProvider {
  const manifest: MeetingProviderManifest = {
    id: opts.id,
    name: opts.name,
    description: opts.description,
    capabilities: {
      embeddable: false,
      attendanceCallback: false,
      recordingFetch: false,
      requiresCredentials: opts.requiresCredentials ?? false,
    },
    configSchema: [
      {
        key: "url",
        label: "URL de la reunión",
        type: "url",
        required: false,
        placeholder: opts.urlPlaceholder,
        help: "Enlace fijo (sala personal). Si la sesión trae su propio enlace, ese tiene prioridad.",
      },
    ],
  };

  return {
    manifest,
    buildJoin(session: LiveSession, cfg: Record<string, unknown>, _user: JoinUser): JoinDescriptor {
      const fromSession = session.meetingUrl?.trim();
      const fromCfg = typeof cfg.url === "string" ? cfg.url.trim() : "";
      const raw = fromSession || fromCfg;
      return {
        mode: "external",
        provider: opts.id,
        url: validateHttpsUrl(raw),
      };
    },
  };
}
